import { useEffect, useRef, useState } from 'react'

import Background from '@/components/Background/Background'
import Logo from '@/components/Logo/Logo'
import { getHomeCompetitor, getAwayCompetitor } from '@/services/jsonDataService'
import { DISPLAY_STATE } from '@/types'

import CricketManualBar from './CricketManualBar'
import CricketManualBug from './CricketManualBug'
import './CricketManualTemplate.css'

import type { JsonMatchData, ScoreBugPosition } from '@/services/jsonDataService'
import type { MatchData } from '@/types'
import type { FC } from 'react'

const ANIMATION_DURATION = 500

export interface CricketManualTemplateProps {
  matchData?: MatchData
  customerId: string
  eventGuid: string
  jsonData: JsonMatchData | null
  displayState: DISPLAY_STATE
  scoreBugPosition: ScoreBugPosition
  isVisible?: boolean
}

const CricketManualTemplate: FC<CricketManualTemplateProps> = ({
  matchData,
  jsonData,
  displayState,
  scoreBugPosition,
  isVisible = true,
}) => {
  const matchConfig = matchData || {}

  const homeCompetitor = getHomeCompetitor(jsonData)
  const awayCompetitor = getAwayCompetitor(jsonData)

  const homeTeam = homeCompetitor?.name || matchConfig.homeTeamConfig?.name || 'Home Team'
  const awayTeam = awayCompetitor?.name || matchConfig.awayTeamConfig?.name || 'Away Team'

  const targetPosition = scoreBugPosition

  const [activePosition, setActivePosition] = useState<ScoreBugPosition>(targetPosition)
  const [bugVisible, setBugVisible] = useState(true)
  const isRepositioning = useRef(false)

  useEffect(() => {
    if (targetPosition === activePosition || isRepositioning.current) return

    isRepositioning.current = true
    setBugVisible(false)

    const timer = setTimeout(() => {
      setActivePosition(targetPosition)
      setBugVisible(true)
      isRepositioning.current = false
    }, ANIMATION_DURATION)

    return () => {
      clearTimeout(timer)
      isRepositioning.current = false
    }
  }, [targetPosition, activePosition])

  const effectiveBugVisible = isVisible && bugVisible

  return (
    <Background>
      <div className="cricket-container">
        <div className="cricket-logo-container">
          <Logo matchConfig={matchConfig} slotId={1} teamName={homeTeam} />
          <Logo matchConfig={matchConfig} slotId={2} teamName={awayTeam} />
        </div>

        {displayState === DISPLAY_STATE.SCOREBAR ? (
          <CricketManualBar matchData={matchData} jsonData={jsonData} isVisible={isVisible} />
        ) : (
          <CricketManualBug
            matchData={matchData}
            jsonData={jsonData}
            isVisible={effectiveBugVisible}
            scorebugPosition={activePosition}
          />
        )}
      </div>
    </Background>
  )
}

export default CricketManualTemplate
